import { buildAttendance } from "./attendance";
import { listHosts } from "./list-hosts";
import type { CatalogPage } from "./catalog-types";
import { CATALOG_DEFAULT_PAGE_SIZE } from "./catalog-query";
import type { EntriesPage, HostPanel } from "./host-panel-types";
import type { HostProfile } from "./host-profile";

/**
 * The panel with no data, for when a read fails for a reason that is not the person's.
 *
 * The design already draws every screen with nothing in it — zero credit, "nenhum presente
 * ainda", the empty list, the empty statement, "Não informado". A failed read lands on those
 * states: no component and no copy exist for failures that the design never drew. What the
 * source would have asserted (status, public address, event) is left empty, and the screens do
 * not render an empty field.
 */

/** A statement with no entries: `EntriesSection` renders its own empty state over it. */
export const EMPTY_ENTRIES_PAGE: EntriesPage = { items: [], totalForLens: 0, nextCursor: null };

/**
 * A catalog with nothing to add — what the add scope draws when the catalog cannot be read.
 *
 * The design already has this state (`CatalogEmpty`), so a failure never becomes a notice of its
 * own: the person keeps the tab, their own list and every other control.
 */
export const EMPTY_CATALOG_PAGE: CatalogPage = {
  items: [],
  facets: { categories: [], collections: [], types: [], materials: [], price: null },
  page: 1,
  pageSize: CATALOG_DEFAULT_PAGE_SIZE,
  totalItems: 0,
  totalPages: 1,
  hasNextPage: false,
};

export function zeroHostPanel(names: { firstName: string; lastName: string }): HostPanel {
  return {
    identity: {
      firstName: names.firstName,
      lastName: names.lastName,
      gender: null,
      eventName: "",
      ...listHosts({}, `${names.firstName} ${names.lastName}`),
    },
    // The prototype's zero-data scenario. Its status copy is not borrowed: it would assert that
    // the list is open, which nobody checked.
    lifecycle: "nova",
    statusTag: "",
    statusLine: "",
    summary: {
      balanceCents: 0,
      inflowCents: 0,
      outflowCents: 0,
      piecesCount: 0,
      piecesLast7Days: 0,
      visitors30d: 0,
      daysToEvent: null,
      eventDateLabel: "",
      eventLocationLabel: "",
    },
    alerts: [],
    attendance: buildAttendance(null),
    ranking: [],
    recentGifts: [],
    publicListUrl: "",
    presentation: { message: "", coverKey: "", coverOptions: [], eventDate: "", eventLocation: "" },
  };
}

/** What the session already knows about the host; the rest reads "Não informado" on the form. */
export function zeroHostProfile(
  session: { sub?: string; cpf?: string; firstName?: string; lastName?: string } | null,
): HostProfile {
  return {
    cpf: session?.cpf ?? "",
    firstName: session?.firstName ?? "",
    lastName: session?.lastName ?? "",
    phone: "",
    birthDate: null,
    email: session?.sub ?? "",
    gender: null,
  };
}
